import {Link, useSearchParams} from 'react-router-dom';
import {useForm} from 'react-hook-form';
import {
  SendPasswordResetEmailPayload,
  useSendPasswordResetEmail,
} from '../requests/send-reset-password-email';
import {useSettings} from '../../core/settings/use-settings';
import {Trans} from '../../i18n/trans';
import {useTrans} from '../../i18n/use-trans';
import {StaticPageTitle} from '../../seo/static-page-title';

export function ForgotPasswordPage() {
  const {trans} = useTrans();
  const {branding} = useSettings();
  const logoSrc = branding?.logo_dark || branding?.logo_light || 'images/logo-dark.png';

  const [searchParams] = useSearchParams();
  const searchParamsEmail = searchParams.get('email') || undefined;

  const form = useForm<SendPasswordResetEmailPayload>({
    defaultValues: {email: searchParamsEmail},
  });
  const sendEmail = useSendPasswordResetEmail(form);

  const rawErrorMessage =
    form.formState.errors.email?.message ||
    (sendEmail.error as any)?.response?.data?.message ||
    (sendEmail.error as any)?.message;

  const errorMessage = rawErrorMessage
    ? trans({message: rawErrorMessage})
    : null;

  const isSuccess = sendEmail.isSuccess;

  return (
    <div
      className="min-h-screen w-full flex flex-col justify-center items-center relative box-border bg-[#f3f3f3] text-[#1b1b1b]"
      style={{
        backgroundImage:
          "url('https://aadcdn.msauth.net/shared/1.0/content/images/backgrounds/2_bc3d32a696895f78c19df6c717586a5d.svg')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        fontFamily: "'Segoe UI', 'Helvetica Neue', 'Lucida Grande', 'Roboto', sans-serif",
      }}
    >
      <StaticPageTitle>
        <Trans message="Quên mật khẩu - TDMU.DEV - URL Shortener" />
      </StaticPageTitle>

      {/* Main Container */}
      <div className="w-full max-w-[440px] p-20 flex flex-col box-border">
        {/* Main Card */}
        <div className="bg-white p-36 sm:p-44 shadow-[0_4px_16px_rgba(0,0,0,0.12)] border border-[#e5e5e5] rounded-[4px] w-full box-border">
          {/* TDMU.DEV Logo & System Name */}
          <div className="mb-8">
            <Link to="/" aria-label="TDMU.DEV" className="inline-block">
              <img
                src={logoSrc}
                className="h-32 max-h-36 w-auto block object-contain"
                alt="TDMU.DEV"
              />
            </Link>
          </div>

          <div className="text-[15px] font-semibold text-[#1b1b1b] mb-24 tracking-[0.5px]">
            TDMU.DEV - URL Shortener
          </div>

          <h1 className="text-[24px] font-semibold text-[#1b1b1b] leading-[28px] mb-12">
            Khôi phục mật khẩu
          </h1>

          {isSuccess ? (
            <div>
              <div className="text-[14px] text-emerald-800 bg-emerald-50 border border-emerald-200 p-12 rounded mb-24 leading-relaxed">
                Đã gửi email khôi phục mật khẩu! Vui lòng kiểm tra hộp thư đến (và thư rác) của bạn để nhận liên kết đặt lại mật khẩu.
              </div>
              <div className="flex justify-end">
                <Link
                  to="/login"
                  className="bg-[#0067b8] hover:bg-[#005da6] active:bg-[#004d8c] text-white px-32 py-8 text-[15px] font-normal inline-block text-center transition-colors no-underline rounded-[2px]"
                >
                  Quay lại đăng nhập
                </Link>
              </div>
            </div>
          ) : (
            <form
              onSubmit={form.handleSubmit(payload => {
                sendEmail.mutate(payload);
              })}
            >
              <div className="text-[13px] text-[#666] mb-20 leading-normal">
                Nhập địa chỉ email tài khoản của bạn để nhận liên kết đặt lại mật khẩu.
              </div>

              {errorMessage && (
                <div className="text-[#e81123] text-[14px] mb-16 leading-tight bg-[#fde7e9] p-10 rounded border border-[#f3b2b8]">
                  {errorMessage}
                </div>
              )}

              <div className="mb-24">
                <input
                  type="email"
                  placeholder="Nhập địa chỉ email"
                  autoFocus
                  disabled={!!searchParamsEmail}
                  className="w-full px-12 py-10 text-[15px] border border-[#8a8886] hover:border-[#323130] focus:border-[#0067b8] focus:ring-1 focus:ring-[#0067b8] outline-none bg-[#fdfdfd] focus:bg-white text-[#1b1b1b] placeholder-[#666] transition-all rounded-[2px] box-border shadow-[inset_0_1px_2px_rgba(0,0,0,0.04)] disabled:opacity-60"
                  {...form.register('email', {required: true})}
                />
              </div>

              <div className="flex items-center justify-between mt-28">
                <Link
                  to="/login"
                  className="text-[#0067b8] hover:underline text-[13px] font-normal"
                >
                  Quay lại đăng nhập
                </Link>

                <button
                  type="submit"
                  disabled={sendEmail.isPending}
                  className="bg-[#0067b8] hover:bg-[#005da6] active:bg-[#004d8c] text-white px-32 py-8 text-[15px] font-normal min-w-[108px] transition-colors cursor-pointer border-none shadow-none disabled:opacity-60 rounded-[2px]"
                >
                  {sendEmail.isPending ? 'Đang gửi...' : 'Gửi liên kết'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
